package org.gymly.theme

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

object GymlyColors {
    val Primary = Color(0xFF0B57D0)
    val OnPrimary = Color(0xFFFFFFFF)
    val Surface = Color(0xFFF6F7FB)
    val SurfaceContainer = Color(0xFFFFFFFF)
    val Outline = Color(0xFFC4C7D0)
    val Text = Color(0xFF1B1B1F)
    val TextMuted = Color(0xFF5F6368)
    val Error = Color(0xFFB3261E)
    val ErrorContainer = Color(0xFFFCE8E6)
    val Success = Color(0xFF2E7D32)
    val WarningContainer = Color(0xFFFFF3E0)
    val SelectedChip = Color(0xFFE3F2FD)
    val PrimaryPressed = Color(0xFF0946AC)
}

object GymlySpace {
    val xs = 6.dp
    val sm = 10.dp
    val md = 16.dp
    val lg = 22.dp
    val xl = 28.dp
}

object GymlyRadius {
    val card = 16.dp
}

object GymlyTouch {
    val minHeight = 48.dp
}

object GymlyFont {
    val title = 22.sp
    val body = 16.sp
    val small = 14.sp
}

/** Color de marca del gimnasio. Si está vacío, se usan los colores por defecto. */
object BrandTheme {
    private val defaultAdminPrimary = Color(0xFF3B4CB8)
    private val defaultAdminPrimaryDark = Color(0xFF2F3D9A)
    private val defaultLogin = Color(0xFF1E2A78)
    private val defaultLoginDeep = Color(0xFF16205C)

    var adminPrimary by mutableStateOf(defaultAdminPrimary)
    var adminPrimaryDark by mutableStateOf(defaultAdminPrimaryDark)
    var loginPanel by mutableStateOf(defaultLogin)
    var loginPanelDeep by mutableStateOf(defaultLoginDeep)

    fun apply(hex: String?) {
        val color = parseHexColor(hex)
        if (color == null) {
            adminPrimary = defaultAdminPrimary
            adminPrimaryDark = defaultAdminPrimaryDark
            loginPanel = defaultLogin
            loginPanelDeep = defaultLoginDeep
            return
        }
        adminPrimary = color
        adminPrimaryDark = darken(color, 0.82f)
        loginPanel = color
        loginPanelDeep = darken(color, 0.72f)
    }

    private fun parseHexColor(raw: String?): Color? {
        val hex = raw?.trim()?.removePrefix("#") ?: return null
        if (hex.length != 6) return null
        val rgb = hex.toLongOrNull(16) ?: return null
        return Color(0xFF000000 or rgb)
    }

    private fun darken(color: Color, factor: Float): Color =
        Color(red = color.red * factor, green = color.green * factor, blue = color.blue * factor, alpha = color.alpha)
}

/** Tokens solo para panel admin (web/desktop). No afectan el portal móvil. */
object AdminColors {
    val Primary: Color get() = BrandTheme.adminPrimary
    val PrimaryDark: Color get() = BrandTheme.adminPrimaryDark
    val BrandPanel = Color(0xFF3B4CB8)
    val OnBrand = Color(0xFFFFFFFF)
    val AccentStat = Color(0xFFD4C4F0)
    val Surface = Color(0xFFF5F6FA)
    val SurfaceContainer = Color(0xFFFFFFFF)
    val Sidebar = Color(0xFF1A1F3D)
    val SidebarText = Color(0xFFB8BBC8)
    val SidebarActive = Color(0xFFFFFFFF)
    val SidebarActiveBg = Color(0xFF2A3158)
    val Text = Color(0xFF1B1B1F)
    val TextMuted = Color(0xFF6B7280)
    val Outline = Color(0xFFD1D5DB)
    val SlotCard = Color(0xFFE8EEFF)
    val Error = Color(0xFFB3261E)
    val Success = Color(0xFF2E7D32)
}

object TileImages {
    const val profile = "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&q=80"
    const val payments = "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&q=80"
    const val bookings = "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=600&q=80"
    const val schedules = "https://images.unsplash.com/photo-1518611012118-696072aa579a?w=600&q=80"
}
